import React, { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Grid, Typography, Button } from '@inplayer-org/inplayer-ui';
import { Field, Formik, FormikProps } from 'formik';
import { array, object, string } from 'yup';

// utils
import map from 'lodash/map';

// constants
import Rules from 'restrictions/roles/constants/rules';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// actions
import {
  deleteAssetCountrySet,
  deleteAllAssetDomains,
  saveAssetRestrictions,
  createTemplateRestrictions,
  updateTemplateRestrictions,
  deleteAssetAgeRestriction,
  deleteTemplateRestrictions,
} from 'ducks/merchantDucks/restrictions/actions';

// components
import Restricted from 'restrictions/roles/components/Restricted';
import Select from 'components/Fields/Select';
import AccordionFooter from 'components/AccordionFooter';
import {
  FullHeightForm,
  ContentContainer,
  CenterContentContainer,
  BlueTypography,
} from 'components/SharedStyledElements';
import FormAccordionHelper, { SectionType } from 'pages/MerchantPages/Assets/components/AccordionHelper';
import AssetDomainsForm from 'pages/MerchantPages/Assets/Restrictions/components/forms/AssetDomainsForm';
import AgeRestrictionForm from 'pages/MerchantPages/Assets/Restrictions/components/forms/subforms/AgeRestrictionsForm';

const { Container, Cell } = Grid;

interface OwnProps {
  closePanel: () => void;
  assetId: number;
  assetDomainRestrictions: Array<Record<string, string>>;
  assetCountrySetId: number;
  isAgeRestrictionEnabled: boolean;
  countrySets: Array<any>;
  ageRestriction: any;
  isTemplate: boolean;
  restrictions: any;
  accordionLabel: string;
}

type Props = OwnProps;

interface FormValues {
  assetCountrySetId: number;
  ageRestriction: any;
  assetDomainRestrictions: Array<Record<string, string>>;
}

const validationSchema = object().shape({
  assetDomainRestrictions: array().of(
    object().shape({
      domain: string().required('*Required'),
    })
  ),
});

const AssetRestrictionsForm: FunctionComponent<Props> = ({
  assetId,
  countrySets,
  ageRestriction,
  assetCountrySetId,
  assetDomainRestrictions,
  isAgeRestrictionEnabled,
  isTemplate,
  closePanel,
  restrictions,
  accordionLabel,
}) => {
  const initialValues: FormValues = {
    assetCountrySetId,
    ageRestriction,
    assetDomainRestrictions,
  };

  const { hasRule } = useRestrictionRules();
  const dispatch = useDispatch();

  const options = map(countrySets, (country) => ({ value: country.id, displayName: country.name, tag: country.name }));

  const handleReset = () => {
    if (assetId !== 0) {
      if (isTemplate) {
        if (assetCountrySetId !== undefined) {
          const { id } = restrictions?.find((restriction: { category: string }) => restriction.category === 'geo');
          dispatch(deleteTemplateRestrictions(assetId, id));
        }
        if (ageRestriction !== -1) {
          const { id } = restrictions?.find((restriction: { category: string }) => restriction.category === 'age');
          dispatch(deleteTemplateRestrictions(assetId, id));
        }

        if (assetDomainRestrictions.length) {
          restrictions
            ?.filter((restriction: { category: string }) => restriction.category === 'domain')
            .forEach((domain: { id: number }) => {
              dispatch(deleteTemplateRestrictions(assetId, domain.id));
            });
        }

        closePanel();
        return;
      }
      dispatch(deleteAssetCountrySet(assetId));
      dispatch(deleteAllAssetDomains(assetId));
      dispatch(deleteAssetAgeRestriction(assetId));
      closePanel();
    }
  };

  const onUpdateRestrictions = (data: any) => {
    const {
      assetCountrySetId: dataAssetCountrySetId,
      assetDomainRestrictions: dataAssetDomain,
      ageRestriction: dataAge,
    } = data;
    if (isTemplate) {
      const isCreateRestriction =
        assetCountrySetId === undefined && ageRestriction === -1 && assetDomainRestrictions === [];
      if (isCreateRestriction) {
        dispatch(
          createTemplateRestrictions({
            itemId: assetId,
            assetCountrySetId: dataAssetCountrySetId,
            assetDomainRestrictions: dataAssetDomain,
            age: dataAge,
          })
        );
      } else if (dataAssetCountrySetId === '') {
        const { id } = restrictions.find((restriction: { category: string }) => restriction.category === 'geo');
        dispatch(deleteTemplateRestrictions(assetId, id));
      } else if (dataAge === '') {
        const { id } = restrictions.find((restriction: { category: string }) => restriction.category === 'age');
        dispatch(deleteTemplateRestrictions(assetId, id));
      } else {
        dispatch(
          updateTemplateRestrictions({
            itemId: assetId,
            assetCountrySetId: dataAssetCountrySetId,
            assetDomainRestrictions: dataAssetDomain,
            age: dataAge,
          })
        );
      }
    } else {
      dispatch(
        saveAssetRestrictions({
          itemId: assetId,
          assetCountrySetId: dataAssetCountrySetId,
          assetDomainRestrictions: dataAssetDomain,
          age: dataAge,
        })
      );
    }

    closePanel();
  };

  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);

  return (
    <Formik
      enableReinitialize
      onSubmit={onUpdateRestrictions}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ values, dirty, isSubmitting, isValid }: FormikProps<FormValues>) => (
        <FullHeightForm>
          <FormAccordionHelper initialValues={initialValues} index={SectionType.Restrictions} closePanel={closePanel} />
          <ContentContainer>
            <CenterContentContainer columns={3} rowGap="3rem">
              <Cell width={3}>
                <Container columns={12} rowGap="2rem">
                  <Cell width={12}>
                    <BlueTypography variant="h5">Geo-restrictions</BlueTypography>
                  </Cell>
                  <Cell width={12}>
                    <Typography variant="p">
                      Make the asset available only in certain countries. The restrictions set here will override the
                      default restrictions set in the Settings.
                    </Typography>
                  </Cell>
                  <Cell width={6}>
                    <Field
                      tag="dropdown_select_country_set"
                      id="country_set"
                      name="assetCountrySetId"
                      component={Select}
                      label={!isReadOnly ? 'Select a country set' : 'Selected Country Set'}
                      defaultOption={{ displayName: 'Select country set' }}
                      options={options}
                      disabled={isReadOnly}
                    />
                  </Cell>
                  <Restricted isRestricted={isReadOnly}>
                    <Cell width={12}>
                      <NavLink to="/settings/auth/geo-restrictions">Manage country sets &rarr;</NavLink>
                    </Cell>
                  </Restricted>
                </Container>
              </Cell>
              <Cell width={3}>
                <Container columns={1} rowGap="2rem">
                  <Cell width={12}>
                    <BlueTypography variant="h5">URL restrictions</BlueTypography>
                  </Cell>
                  <Cell width={12}>
                    {assetDomainRestrictions.length === 0 && (
                      <Typography variant="p">
                        Make the asset available only on certain URLs. The restrictions set here will override the
                        default restrictions set in the Settings.
                      </Typography>
                    )}
                    <AssetDomainsForm
                      assetDomainRestrictions={values.assetDomainRestrictions}
                      isReadOnly={isReadOnly}
                      isTemplate={isTemplate}
                    />
                    <Restricted isRestricted={isReadOnly}>
                      <NavLink to="/settings/auth/domain-restrictions">Manage default URLs &rarr;</NavLink>
                    </Restricted>
                  </Cell>
                </Container>
              </Cell>
              <Cell width={3}>
                <Container columns={12} rowGap="2rem">
                  <Cell width={12}>
                    <BlueTypography variant="h5">Age restrictions</BlueTypography>
                  </Cell>
                  <AgeRestrictionForm
                    age={ageRestriction}
                    isAgeRestrictionEnabled={isAgeRestrictionEnabled}
                    isAssetRestriction
                  />
                  {isAgeRestrictionEnabled && (
                    <Restricted isRestricted={isReadOnly}>
                      <Cell width={12}>
                        <NavLink to="/settings/auth/age-restrictions">Manage default minimum age &rarr;</NavLink>
                      </Cell>
                    </Restricted>
                  )}
                </Container>
              </Cell>
              <Restricted isRestricted={isReadOnly}>
                <Cell width={1}>
                  <Button tag="button_reset" type="button" size="sm" onClick={handleReset}>
                    Reset to default
                  </Button>
                </Cell>
              </Restricted>
            </CenterContentContainer>
            <AccordionFooter
              accordionLabel={accordionLabel}
              handleCancel={closePanel}
              disabled={!dirty || isSubmitting || !isValid}
            />
          </ContentContainer>
        </FullHeightForm>
      )}
    </Formik>
  );
};

export default AssetRestrictionsForm;
